import React, { useState, useEffect } from 'react';
import api from '../api';

const VoiceProfileSetup = ({ onComplete, isModal = false }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingProfile, setExistingProfile] = useState(null);
  
  const [formData, setFormData] = useState({
    writing_style: 'professional',
    formality_level: 3,
    typical_opener: '',
    sign_off: 'Best',
    sample_emails: ['', '', ''],
    preferred_phrases: [],
    avoid_phrases: [],
    additional_context: ''
  });
  
  const [phraseInput, setPhraseInput] = useState('');
  const [avoidInput, setAvoidInput] = useState('');

  useEffect(() => {
    fetchExistingProfile();
  }, []);

  const fetchExistingProfile = async () => {
    setLoading(true);
    try {
      const res = await api.get('/ai/voice-profile');
      if (res.data.profile) {
        setExistingProfile(res.data.profile);
        setFormData({
          writing_style: res.data.profile.writing_style || 'professional',
          formality_level: res.data.profile.formality_level || 3,
          typical_opener: res.data.profile.typical_opener || '',
          sign_off: res.data.profile.sign_off || 'Best',
          sample_emails: res.data.profile.sample_emails?.length > 0 
            ? [...res.data.profile.sample_emails, '', '', ''].slice(0, 3)
            : ['', '', ''],
          preferred_phrases: res.data.profile.preferred_phrases || [],
          avoid_phrases: res.data.profile.avoid_phrases || [],
          additional_context: res.data.profile.additional_context || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Filter out empty sample emails
      const cleanedData = {
        ...formData,
        sample_emails: formData.sample_emails.filter(e => e.trim().length > 0)
      };
      
      await api.post('/ai/voice-profile', cleanedData);
      
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      alert('Failed to save voice profile');
    } finally {
      setSaving(false);
    }
  };

  const addPreferredPhrase = () => {
    if (phraseInput.trim()) {
      setFormData({
        ...formData,
        preferred_phrases: [...formData.preferred_phrases, phraseInput.trim()]
      });
      setPhraseInput('');
    }
  };

  const removePreferredPhrase = (index) => {
    setFormData({
      ...formData,
      preferred_phrases: formData.preferred_phrases.filter((_, i) => i !== index)
    });
  };

  const addAvoidPhrase = () => {
    if (avoidInput.trim()) {
      setFormData({
        ...formData,
        avoid_phrases: [...formData.avoid_phrases, avoidInput.trim()]
      });
      setAvoidInput('');
    }
  };

  const removeAvoidPhrase = (index) => {
    setFormData({
      ...formData,
      avoid_phrases: formData.avoid_phrases.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-zinc-400">Loading...</div>
      </div>
    );
  }

  const containerClass = isModal 
    ? "p-6" 
    : "bg-zinc-900 border border-zinc-800 rounded-xl p-8 max-w-3xl mx-auto";

  return (
    <div className={containerClass}>
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">
          {existingProfile?.setup_completed ? 'Edit Your Voice Profile' : 'Set Up Your Writing Voice'}
        </h2>
        <p className="text-zinc-400">
          Help AI understand how you write so emails sound like you, not a robot.
        </p>
      </div>

      {/* Progress indicator */}
      <div className="flex gap-2 mb-8">
        {[1, 2, 3, 4].map(s => (
          <div
            key={s}
            className={`h-2 flex-1 rounded-full transition-colors ${
              s <= step ? 'bg-nextide-turquoise' : 'bg-zinc-700'
            }`}
          />
        ))}
      </div>

      {/* Step 1: Writing Style */}
      {step === 1 && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-white">How would you describe your writing style?</h3>
          
          <div className="grid grid-cols-2 gap-4">
            {[
              { value: 'casual', label: 'Casual', desc: 'Friendly, conversational, relaxed' },
              { value: 'professional', label: 'Professional', desc: 'Business appropriate, clear, direct' },
              { value: 'direct', label: 'Direct', desc: 'Straight to the point, no fluff' },
              { value: 'warm', label: 'Warm', desc: 'Personable, relationship-focused' }
            ].map(style => (
              <button
                key={style.value}
                onClick={() => setFormData({ ...formData, writing_style: style.value })}
                className={`p-4 rounded-xl border text-left transition-colors ${
                  formData.writing_style === style.value
                    ? 'bg-nextide-turquoise/20 border-nextide-turquoise text-white'
                    : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                }`}
              >
                <div className="font-medium">{style.label}</div>
                <div className="text-sm text-zinc-400 mt-1">{style.desc}</div>
              </button>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-3">
              Formality Level
            </label>
            <div className="flex items-center gap-4">
              <span className="text-zinc-500 text-sm">Casual</span>
              <input
                type="range"
                min="1"
                max="5"
                value={formData.formality_level}
                onChange={(e) => setFormData({ ...formData, formality_level: parseInt(e.target.value) })}
                className="flex-1 accent-nextide-turquoise"
              />
              <span className="text-zinc-500 text-sm">Formal</span>
            </div>
            <div className="text-center text-nextide-turquoise mt-2">
              {formData.formality_level}/5
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => setStep(2)}
              className="bg-nextide-turquoise text-black px-6 py-2 rounded-lg font-semibold hover:opacity-90"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Sample Emails */}
      {step === 2 && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-white">Paste 1-3 cold emails you've written</h3>
            <p className="text-zinc-400 text-sm mt-1">
              These help AI learn your actual writing patterns. The more examples, the better.
            </p>
          </div>
          
          {formData.sample_emails.map((email, index) => (
            <div key={index}>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Sample Email {index + 1} {index === 0 ? '(recommended)' : '(optional)'}
              </label>
              <textarea
                value={email}
                onChange={(e) => {
                  const newEmails = [...formData.sample_emails];
                  newEmails[index] = e.target.value;
                  setFormData({ ...formData, sample_emails: newEmails });
                }}
                placeholder="Paste a cold email you've sent before..."
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-nextide-turquoise min-h-[120px] resize-none"
              />
            </div>
          ))}

          <div className="flex justify-between">
            <button
              onClick={() => setStep(1)}
              className="text-zinc-400 hover:text-white px-4 py-2"
            >
              Back
            </button>
            <button
              onClick={() => setStep(3)}
              className="bg-nextide-turquoise text-black px-6 py-2 rounded-lg font-semibold hover:opacity-90"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Preferences */}
      {step === 3 && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-white">Your preferences</h3>
          
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              How do you typically sign off?
            </label>
            <div className="flex flex-wrap gap-2">
              {['Best', 'Thanks', 'Cheers', 'Talk soon', 'Looking forward'].map(signOff => (
                <button
                  key={signOff}
                  onClick={() => setFormData({ ...formData, sign_off: signOff })}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    formData.sign_off === signOff
                      ? 'bg-nextide-turquoise text-black'
                      : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                  }`}
                >
                  {signOff}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={formData.sign_off}
              onChange={(e) => setFormData({ ...formData, sign_off: e.target.value })}
              placeholder="Or type your own..."
              className="w-full mt-3 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white placeholder-zinc-500 focus:outline-none focus:border-nextide-turquoise"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              Phrases you like to use
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={phraseInput}
                onChange={(e) => setPhraseInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addPreferredPhrase()}
                placeholder='e.g., "quick question", "wanted to flag"'
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white placeholder-zinc-500 focus:outline-none focus:border-nextide-turquoise"
              />
              <button
                onClick={addPreferredPhrase}
                className="bg-zinc-700 text-white px-4 py-2 rounded-lg hover:bg-zinc-600"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.preferred_phrases.map((phrase, i) => (
                <span
                  key={i}
                  className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                >
                  {phrase}
                  <button onClick={() => removePreferredPhrase(i)} className="hover:text-white">x</button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              Phrases to avoid
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={avoidInput}
                onChange={(e) => setAvoidInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addAvoidPhrase()}
                placeholder='e.g., "just following up", "circling back"'
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white placeholder-zinc-500 focus:outline-none focus:border-nextide-turquoise"
              />
              <button
                onClick={addAvoidPhrase}
                className="bg-zinc-700 text-white px-4 py-2 rounded-lg hover:bg-zinc-600"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.avoid_phrases.map((phrase, i) => (
                <span
                  key={i}
                  className="bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                >
                  {phrase}
                  <button onClick={() => removeAvoidPhrase(i)} className="hover:text-white">x</button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep(2)}
              className="text-zinc-400 hover:text-white px-4 py-2"
            >
              Back
            </button>
            <button
              onClick={() => setStep(4)}
              className="bg-nextide-turquoise text-black px-6 py-2 rounded-lg font-semibold hover:opacity-90"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Additional Context & Confirm */}
      {step === 4 && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-white">Anything else?</h3>
          
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              Additional context about your writing style
            </label>
            <textarea
              value={formData.additional_context}
              onChange={(e) => setFormData({ ...formData, additional_context: e.target.value })}
              placeholder="Anything else the AI should know about how you communicate? Your background, industry experience, what makes your style unique..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-nextide-turquoise min-h-[100px] resize-none"
            />
          </div>

          {/* Summary */}
          <div className="bg-zinc-800 rounded-xl p-4">
            <h4 className="text-sm font-medium text-zinc-400 mb-3">Your Voice Profile Summary</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-500">Style:</span>
                <span className="text-white capitalize">{formData.writing_style}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Formality:</span>
                <span className="text-white">{formData.formality_level}/5</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Sign-off:</span>
                <span className="text-white">{formData.sign_off}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Sample emails:</span>
                <span className="text-white">{formData.sample_emails.filter(e => e.trim()).length} provided</span>
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep(3)}
              className="text-zinc-400 hover:text-white px-4 py-2"
            >
              Back
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-nextide-turquoise text-black px-8 py-2 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Voice Profile'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceProfileSetup;
